<template>
  <div class="announcement-card">
    <!-- Header with title and Add New button -->
    <div class="announcement-card__header">
      <h3 class="announcement-card__title">Announcements</h3>
      
      <!-- Add New button (only visible to admins) -->
      <button 
        v-if="isAdmin && !isCreatingNew"
        @click="startNewAnnouncement"
        class="announcement-card__add-btn"
        aria-label="Add new announcement"
        :disabled="isLoading"
      >
        <i class="fas fa-plus"></i>
        Add New
      </button>
    </div>
    
    <!-- New Announcement Creation Form -->
    <div 
      v-if="isCreatingNew" 
      class="announcement-card__new-form"
    >
      <div class="announcement-card__new-form-content">
        <textarea
          :value="newAnnouncementText"
          @input="updateNewAnnouncementText($event.target.value)"
          class="announcement-card__textarea"
          rows="3"
          placeholder="Enter new announcement text..."
          :disabled="isSavingNew"
          autofocus
        ></textarea>
        
        <!-- Error message for new announcement -->
        <div v-if="newAnnouncementError" class="announcement-card__error">
          {{ newAnnouncementError }}
        </div>
        
        <!-- Action buttons for new announcement -->
        <div class="announcement-card__actions">
          <button 
            @click="handleSaveNew"
            class="announcement-card__btn announcement-card__btn--save"
            :disabled="isSavingNew"
          >
            <i class="fas fa-check"></i>
            {{ isSavingNew ? 'Creating...' : 'Create' }}
          </button>
          <button 
            @click="cancelNewAnnouncement"
            class="announcement-card__btn announcement-card__btn--cancel"
            :disabled="isSavingNew"
          >
            <i class="fas fa-times"></i>
            Cancel
          </button>
        </div>
      </div>
      <div class="announcement-card__divider"></div>
    </div>
    
    <!-- Loading state -->
    <div v-if="isLoading" class="announcement-card__loading">
      Loading announcements...
    </div>
    
    <!-- Announcements list -->
    <div v-else-if="announcements?.length > 0" class="announcement-card__content">
      <div 
        v-for="(announcement, index) in announcements" 
        :key="announcement.id"
        class="announcement-card__item"
        :class="{ 
          'announcement-card__item--editing': isEditing(index),
          'announcement-card__item--deleting': isDeleting(index)
        }"
      >
        <div class="announcement-card__divider" v-if="index > 0"></div>
        
        <!-- View Mode -->
        <div v-if="!isEditing(index)" class="announcement-card__view-mode">
          <p class="announcement-card__text">{{ announcement.text }}</p>
          
          <!-- Edit button (only visible to admins) -->
          <button 
            v-if="isAdmin"
            @click="startEdit(index, announcement.text)"
            class="announcement-card__edit-btn"
            aria-label="Edit announcement"
            :disabled="isDeleting(index)"
          >
            <i class="fas fa-edit"></i>
          </button>
        </div>
        
        <!-- Edit Mode -->
        <div v-else class="announcement-card__edit-mode">
          <textarea
            :value="getEditValue(index)"
            @input="updateEditValue(index, $event.target.value)"
            class="announcement-card__textarea"
            rows="3"
            placeholder="Enter announcement text..."
            :disabled="isSaving(index) || isDeleting(index)"
          ></textarea>
          
          <!-- Error message -->
          <div v-if="getError(index)" class="announcement-card__error">
            {{ getError(index) }}
          </div>
          
          <!-- Action buttons -->
          <div class="announcement-card__actions">
            <button 
              @click="handleSaveEdit(index, announcement)"
              class="announcement-card__btn announcement-card__btn--save"
              :disabled="isSaving(index) || isDeleting(index)"
            >
              <i class="fas fa-check"></i>
              {{ isSaving(index) ? 'Saving...' : 'Save' }}
            </button>
            <button 
              @click="handleDelete(index, announcement)"
              class="announcement-card__btn announcement-card__btn--delete"
              :disabled="isSaving(index) || isDeleting(index)"
            >
              <i class="fas fa-trash"></i>
              {{ isDeleting(index) ? 'Deleting...' : 'Delete' }}
            </button>
            <button 
              @click="cancelEdit(index)"
              class="announcement-card__btn announcement-card__btn--cancel"
              :disabled="isSaving(index) || isDeleting(index)"
            >
              <i class="fas fa-times"></i>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Empty state -->
    <div v-else class="announcement-card__empty">
      <p>No announcements at this time.</p>
      <p v-if="isAdmin" class="announcement-card__empty-hint">
        Click "Add New" to create the first announcement.
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useAnnouncementEdit } from '@/composables/useAnnouncementEdit'
import {useUserStore} from "@/store/user"
const userStore = useUserStore()
const props = defineProps({
  announcements: {
    type: Array,
    default: () => []
  },
  isLoading: {
    type: Boolean,
    default: false
  }
})

// Emit definition
const emit = defineEmits(['refresh'])

const isAdmin = computed(() => userStore.isSysAdmin)

// Initialize announcement editing composable
const {
  // Edit state
  startEdit,
  cancelEdit,
  isEditing,
  isSaving,
  isDeleting,
  getEditValue,
  updateEditValue,
  getError,
  saveEdit,
  
  // New announcement state
  isCreatingNew,
  newAnnouncementText,
  newAnnouncementError,
  isSavingNew,
  
  // New announcement functions
  deleteAnnouncement,
  startNewAnnouncement,
  cancelNewAnnouncement,
  updateNewAnnouncementText,
  saveNewAnnouncement
} = useAnnouncementEdit(props.announcements)

/**
 * Handle save edit button click
 * Calls saveEdit and refreshes parent on success
 */
const handleSaveEdit = async (index, announcement) => {
  const success = await saveEdit(index, announcement)
  if (success) {
    emit('refresh')
  }
}

/**
 * Handle delete button click
 * Calls deleteAnnouncement and refreshes parent on success
 */
const handleDelete = async (index, announcement) => {
  const success = await deleteAnnouncement(index, announcement)
  if (success) {
    emit('refresh')
  }
}

/**
 * Handle save new announcement
 * Calls saveNewAnnouncement and refreshes parent on success
 */
const handleSaveNew = async () => {
  const success = await saveNewAnnouncement()
  if (success) {
    emit('refresh')
  }
}
</script>

<style scoped>
.announcement-card {
  background: linear-gradient(135deg, #d4845f 0%, #c17352 100%);
  border-radius: 12px;
  padding: 1.5rem;
  color: white;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.announcement-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
}

.announcement-card__title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  color: white;
}

.announcement-card__add-btn {
  background: rgba(76, 175, 80, 0.9);
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  color: white;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
}

.announcement-card__add-btn:hover:not(:disabled) {
  background: rgba(76, 175, 80, 1);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.announcement-card__add-btn:active:not(:disabled) {
  transform: translateY(0);
}

.announcement-card__add-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.announcement-card__new-form {
  margin-bottom: 1rem;
}

.announcement-card__new-form-content {
  background: rgba(255, 255, 255, 0.1);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.announcement-card__content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
}

.announcement-card__item {
  display: flex;
  flex-direction: column;
  position: relative;
  transition: all 0.3s ease;
}

.announcement-card__item--editing {
  background: rgba(255, 255, 255, 0.1);
  padding: 1rem;
  border-radius: 8px;
}

.announcement-card__item--deleting {
  opacity: 0.6;
  pointer-events: none;
}

.announcement-card__divider {
  height: 2px;
  background: rgba(255, 255, 255, 0.3);
  margin-bottom: 1rem;
}

.announcement-card__view-mode {
  position: relative;
  padding-right: 2.5rem;
}

.announcement-card__text {
  font-size: 0.9rem;
  line-height: 1.6;
  margin: 0;
  color: rgba(255, 255, 255, 0.95);
  word-wrap: break-word;
  white-space: pre-wrap;
}

.announcement-card__edit-btn {
  position: absolute;
  top: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.2s ease;
  opacity: 0.7;
}

.announcement-card__edit-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
  opacity: 1;
  transform: scale(1.05);
}

.announcement-card__edit-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.announcement-card__edit-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.announcement-card__edit-mode {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.announcement-card__textarea {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.95);
  color: #333;
  font-size: 0.9rem;
  font-family: inherit;
  line-height: 1.6;
  resize: vertical;
  min-height: 80px;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.announcement-card__textarea:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.6);
  background: white;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.2);
}

.announcement-card__textarea:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.announcement-card__error {
  font-size: 0.85rem;
  color: #ffebee;
  background: rgba(244, 67, 54, 0.8);
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  margin-top: -0.25rem;
}

.announcement-card__actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.announcement-card__btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.announcement-card__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.announcement-card__btn--save {
  background: rgba(76, 175, 80, 0.9);
  color: white;
}

.announcement-card__btn--save:hover:not(:disabled) {
  background: rgba(76, 175, 80, 1);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.announcement-card__btn--delete {
  background: rgba(244, 67, 54, 0.9);
  color: white;
}

.announcement-card__btn--delete:hover:not(:disabled) {
  background: rgba(244, 67, 54, 1);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.announcement-card__btn--cancel {
  background: rgba(255, 255, 255, 0.3);
  color: white;
}

.announcement-card__btn--cancel:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.4);
  transform: translateY(-1px);
}

.announcement-card__btn:active:not(:disabled) {
  transform: translateY(0);
}

.announcement-card__loading {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  padding: 2rem;
  font-style: italic;
}

.announcement-card__empty {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  padding: 2rem;
  font-style: italic;
}

.announcement-card__empty p {
  margin: 0.5rem 0;
}

.announcement-card__empty-hint {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
}

@media (max-width: 768px) {
  .announcement-card {
    min-height: 200px;
    padding: 1rem;
  }

  .announcement-card__header {
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 1rem;
  }

  .announcement-card__title {
    font-size: 1.25rem;
  }

  .announcement-card__add-btn {
    width: 100%;
    justify-content: center;
  }

  .announcement-card__text {
    font-size: 0.85rem;
  }

  .announcement-card__edit-btn {
    width: 28px;
    height: 28px;
    font-size: 0.85rem;
  }

  .announcement-card__textarea {
    font-size: 0.85rem;
    padding: 0.6rem;
  }

  .announcement-card__btn {
    padding: 0.4rem 0.75rem;
    font-size: 0.8rem;
  }
  
  .announcement-card__actions {
    flex-direction: column-reverse;
  }
  
  .announcement-card__btn {
    width: 100%;
    justify-content: center;
  }
}
</style>